import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import { EVA_URL } from 'helpers/urlHelper';
import { getNCFrameSummary } from 'services/productionOverviewService';
import { getRightFirstTimePercent, summarizeCheckedItemsByProductType } from './qualitySummaryHelpers';
import QualitySummaryCard from './QualitySummaryCard';
import QualityItemsDetailModal from './QualityItemsDetailModal';

const FRAME_CHECKING_DASHBOARD_URL = `${EVA_URL}/Internal/EmplasScanStation/FrameCheckingDashboard.aspx`;

const CHECKED_ITEM_COLUMNS = [
  { key: 'jobNo', label: 'Job No' },
  { key: 'customer', label: 'Customer' },
  { key: 'winNo', label: 'Win No' },
  { key: 'lastLocation', label: 'Last Location' },
  { key: 'isChecked', label: 'Checked?', render: item => (item.isChecked ? 'Yes' : 'No') },
  { key: 'status', label: 'Status', render: item => item.status || '—' }
];

export default function QualityCheckedSummary({ date }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getNCFrameSummary(date, date)
      .then(result => {
        if (cancelled) return;
        const data = result?.data?.data ?? result?.data ?? [];
        setItems(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        if (!cancelled) console.error('Error fetching N/C frame summary', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date]);

  const cards = useMemo(
    () =>
      summarizeCheckedItemsByProductType(items).map(item => ({
        ...item,
        rftPercent: getRightFirstTimePercent(item.totalChecked, item.totalRejected)
      })),
    [items]
  );

  return (
    <section className="quality-summary-section" style={{ marginTop: 20 }}>
      <div style={{ marginBottom: 10 }}>
        <h4 className="quality-summary-section-title" style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
          Checked Summary — Frames N/C, Ancillary, Hardware, Welded Cills
        </h4>
        <a href={FRAME_CHECKING_DASHBOARD_URL} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
          View Frame Checking Dashboard
        </a>
      </div>

      {loading ? (
        <div className="text-center py-3">
          <Spinner variant="primary" animation="border" role="status" />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {cards.map(c => (
            <QualitySummaryCard
              key={c.key}
              label={c.label}
              rftPercent={c.rftPercent}
              stats={[
                {
                  label: 'Overall',
                  value: c.totalScheduled,
                  onClick: () => setDetail({ title: `${c.label} — Overall`, items: c.overallItems })
                },
                {
                  label: 'To Check',
                  value: c.totalToCheck,
                  onClick: () => setDetail({ title: `${c.label} — To Check`, items: c.toCheckItems })
                },
                {
                  label: 'Checked',
                  value: c.totalChecked,
                  onClick: () => setDetail({ title: `${c.label} — Checked`, items: c.checkedItems })
                },
                {
                  label: 'Rejected',
                  value: c.totalRejected,
                  onClick: () => setDetail({ title: `${c.label} — Rejected`, items: c.rejectedItems })
                }
              ]}
            />
          ))}
        </div>
      )}

      <QualityItemsDetailModal
        show={!!detail}
        onHide={() => setDetail(null)}
        title={detail?.title || ''}
        items={detail?.items || []}
        columns={CHECKED_ITEM_COLUMNS}
      />
    </section>
  );
}

QualityCheckedSummary.propTypes = {
  date: PropTypes.string.isRequired
};
